import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import type {
  ImagePreviewType,
  ProductCategory,
  RequestDetail,
  RequestListItem,
  RequestStats,
} from "@/components/ProductRequestsParent/types";

// ============================================================================
// State Interface
// ============================================================================

interface ProductRequestsState {
  // Stats/KPI
  stats: RequestStats | null;

  // Requests list
  requests: RequestListItem[];
  requestsPagination: { count: number; page: number; pageSize: number };

  // Request detail modal
  detailModal: { open: boolean; requestId: string | null };
  selectedRequest: RequestDetail | null;
  selectedRequestLoading: boolean;
  selectedRequestError: string | null;

  // Action modals (triggered from detail modal)
  rejectModal: { open: boolean };
  approveModal: { open: boolean };
  requestInfoModal: { open: boolean };

  // Categories (used for approve modal category selection)
  categories: ProductCategory[];

  // Image preview modal
  imagePreviewModal: {
    open: boolean;
    imageType: ImagePreviewType | null;
    imageUrl: string | null;
  };
}

// ============================================================================
// Initial State
// ============================================================================

const initialState: ProductRequestsState = {
  stats: null,
  requests: [],
  requestsPagination: { count: 0, page: 1, pageSize: 10 },
  detailModal: { open: false, requestId: null },
  selectedRequest: null,
  selectedRequestLoading: false,
  selectedRequestError: null,
  rejectModal: { open: false },
  approveModal: { open: false },
  requestInfoModal: { open: false },
  categories: [],
  imagePreviewModal: { open: false, imageType: null, imageUrl: null },
};

// ============================================================================
// Slice
// ============================================================================

const productRequestsSlice = createSlice({
  name: "productRequests",
  initialState,
  reducers: {
    // Stats
    setStats: (state, action: PayloadAction<RequestStats>) => {
      state.stats = action.payload;
    },

    // Requests list
    setRequests: (state, action: PayloadAction<RequestListItem[]>) => {
      state.requests = action.payload;
    },
    setRequestsPagination: (
      state,
      action: PayloadAction<{
        count: number;
        page: number;
        pageSize: number;
      }>,
    ) => {
      state.requestsPagination = action.payload;
    },

    // Detail modal
    openDetailModal: (state, action: PayloadAction<string>) => {
      state.detailModal = { open: true, requestId: action.payload };
    },
    closeDetailModal: (state) => {
      state.detailModal = { open: false, requestId: null };
      state.selectedRequest = null;
      state.selectedRequestLoading = false;
      state.selectedRequestError = null;
    },
    setSelectedRequest: (
      state,
      action: PayloadAction<RequestDetail | null>,
    ) => {
      state.selectedRequest = action.payload;
    },
    setSelectedRequestLoading: (state, action: PayloadAction<boolean>) => {
      state.selectedRequestLoading = action.payload;
    },
    setSelectedRequestError: (state, action: PayloadAction<string | null>) => {
      state.selectedRequestError = action.payload;
    },

    // Reject modal
    openRejectModal: (state) => {
      state.rejectModal = { open: true };
    },
    closeRejectModal: (state) => {
      state.rejectModal = { open: false };
    },

    // Approve modal
    openApproveModal: (state) => {
      state.approveModal = { open: true };
    },
    closeApproveModal: (state) => {
      state.approveModal = { open: false };
    },

    // Request info modal
    openRequestInfoModal: (state) => {
      state.requestInfoModal = { open: true };
    },
    closeRequestInfoModal: (state) => {
      state.requestInfoModal = { open: false };
    },

    // Categories
    setCategories: (state, action: PayloadAction<ProductCategory[]>) => {
      state.categories = action.payload;
    },

    // Image preview modal
    openImagePreview: (
      state,
      action: PayloadAction<{
        imageType: ImagePreviewType;
        imageUrl: string | null;
      }>,
    ) => {
      state.imagePreviewModal = {
        open: true,
        imageType: action.payload.imageType,
        imageUrl: action.payload.imageUrl,
      };
    },
    closeImagePreview: (state) => {
      state.imagePreviewModal = {
        open: false,
        imageType: null,
        imageUrl: null,
      };
    },
  },
});

// ============================================================================
// Exports
// ============================================================================

export const {
  setStats,
  setRequests,
  setRequestsPagination,
  openDetailModal,
  closeDetailModal,
  setSelectedRequest,
  setSelectedRequestLoading,
  setSelectedRequestError,
  openRejectModal,
  closeRejectModal,
  openApproveModal,
  closeApproveModal,
  openRequestInfoModal,
  closeRequestInfoModal,
  setCategories,
  openImagePreview,
  closeImagePreview,
} = productRequestsSlice.actions;

export default productRequestsSlice.reducer;
